import { useState, useEffect } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Ticket, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-transparent border-b border-white/20 focus:border-lima-blue outline-none text-lima-paper text-sm px-0 py-3 placeholder:text-white/30 transition-colors duration-200 rounded-none";

export default function RsvpDialog({ event, open, onOpenChange }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) setForm({ name: "", phone: "", email: "" });
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/rsvps", { ...form, event: event.title });
      toast.success(`Spot reserved for "${event.title}" — we'll confirm shortly.`);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (!event) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-none bg-lima-ink border-white/15 text-lima-paper" data-testid="rsvp-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-2xl tracking-tight text-lima-paper leading-tight">
            {event.title}
          </DialogTitle>
          <DialogDescription className="font-mono2 text-[11px] tracking-[0.15em] text-white/50">
            {event.date_text} · {event.location}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-6 mt-4" data-testid="rsvp-form">
          <div>
            <label className="block font-mono2 text-[11px] tracking-[0.25em] text-white/40 mb-1">NAME *</label>
            <input required data-testid="rsvp-name-input" className={inputCls} placeholder="Your full name" value={form.name} onChange={set("name")} />
          </div>
          <div>
            <label className="block font-mono2 text-[11px] tracking-[0.25em] text-white/40 mb-1">PHONE *</label>
            <input required data-testid="rsvp-phone-input" className={inputCls} placeholder="+91 …" value={form.phone} onChange={set("phone")} />
          </div>
          <div>
            <label className="block font-mono2 text-[11px] tracking-[0.25em] text-white/40 mb-1">EMAIL *</label>
            <input required type="email" data-testid="rsvp-email-input" className={inputCls} placeholder="you@email.com" value={form.email} onChange={set("email")} />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} data-testid="rsvp-cancel"
              className="border border-white/20 text-white/70 px-5 py-3 text-sm font-semibold hover:bg-white/5 transition-colors duration-200 flex items-center gap-2">
              <X size={14} /> Cancel
            </button>
            <motion.button
              whileTap={{ scale: 0.96 }}
              type="submit"
              disabled={sending}
              data-testid="rsvp-submit-button"
              className="bg-lima-blue hover:bg-lima-bluehover disabled:opacity-50 text-white font-mono2 text-xs font-bold tracking-[0.2em] px-7 py-3 transition-colors duration-200 flex items-center gap-3"
            >
              <Ticket size={15} /> {sending ? "RESERVING…" : "RESERVE"}
            </motion.button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
